import { useCallback, useEffect, useReducer } from 'react';
import { useSelector } from 'react-redux';
import { selectClient } from '@/model';
import {
  useBenefitDesignInputs,
  useBenefitDesignInputsActions,
} from '@/features/shop-insurance/stores/benefit-design-inputs-store';
import {
  useHiddenCarriers,
  useCarrierExclusionsActions,
} from '@/features/shop-insurance/stores/carrier-exclusions-store';
import type {
  BenefitDesignInputs,
  BenefitPaymentType,
  MonthlyBenefitTiming,
} from '@/features/shop-insurance/types';
import { MAX_INFLATION_PERCENT } from '@/features/shop-insurance/hooks/use-adjusted-quotes';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { getLtcTiming } from '@/features/shop-insurance/lib/ltc-timing';

type SplitMode = 'combined' | 'separate';
type InflationTypeFilter = 'all' | 'simple' | 'compound';
type BenefitPaymentTypeFilter = BenefitPaymentType | 'all';

export interface DraftState {
  monthlyBenefit: string;
  monthlyBenefitTiming: MonthlyBenefitTiming;
  durationType: SplitMode;
  duration: string;
  aobDuration: string;
  cobDuration: string;
  /**
   * Minimum inflation protection % when `inflationProtectionMinimum` is on; the single
   * combined-mode rate when it is off.
   */
  inflation: string;
  /** Legacy separate-mode fields, used only while `inflationProtectionMinimum` is off. */
  inflationType: SplitMode;
  aobInflation: string;
  cobInflation: string;
  inflationTypeFilter: InflationTypeFilter;
  draftHiddenCarriers: string[];
  draftBenefitPaymentType: BenefitPaymentTypeFilter;
}

type DraftAction =
  | { type: 'SET_FIELD'; field: keyof DraftState; value: string }
  | { type: 'SET_MONTHLY_BENEFIT_TIMING'; value: MonthlyBenefitTiming }
  | { type: 'SET_DURATION_TYPE'; value: SplitMode }
  | { type: 'SET_INFLATION_TYPE'; value: SplitMode }
  | { type: 'SET_INFLATION_TYPE_FILTER'; value: InflationTypeFilter }
  | { type: 'TOGGLE_CARRIER'; carrier: string }
  | { type: 'SET_BENEFIT_PAYMENT_TYPE'; value: BenefitPaymentTypeFilter }
  | { type: 'HYDRATE'; value: DraftState }
  | { type: 'RESET' };

export type BenefitDesignAppliedFilterRemoval =
  | { type: 'benefitPaymentType' }
  | { type: 'benefitDuration' }
  | { type: 'inflationProtection' }
  | { type: 'inflationType' }
  | { type: 'carrier'; carrier: string };

function parseCurrency(raw: string): number | null {
  const n = Number(raw.replace(/[^0-9.]/g, ''));
  return isNaN(n) || n <= 0 ? null : n;
}

function parsePercent(raw: string): number | null {
  const stripped = raw.replace(/[^0-9.]/g, '');
  if (stripped === '') return null;
  const n = Number(stripped);
  return isNaN(n) || n < 0 || n > 5 ? null : n;
}

function formatInflationDraft([min, max]: number[]): string {
  return min === 0 && max === MAX_INFLATION_PERCENT ? '' : String(min);
}

export function isLifetime(raw: string): boolean {
  return raw.trim().toLowerCase() === 'lifetime';
}

function formatDurationDraft(value: number): string {
  return value === Infinity ? 'Lifetime' : String(value);
}

function parseDuration(raw: string): number | null {
  if (isLifetime(raw)) return Infinity;
  const n = Number(raw.replace(/[^0-9.]/g, ''));
  return isNaN(n) || n <= 0 ? null : n;
}

function initializeDraft({
  inputs,
  hiddenCarriers,
}: {
  inputs: BenefitDesignInputs;
  hiddenCarriers: string[];
}): DraftState {
  return {
    monthlyBenefit: inputs.monthlyBenefit !== null ? String(inputs.monthlyBenefit) : '',
    monthlyBenefitTiming: inputs.monthlyBenefitTiming ?? 'policy-issue',
    durationType: inputs.benefitDuration?.type ?? 'combined',
    duration: inputs.benefitDuration ? formatDurationDraft(inputs.benefitDuration.combined[0]) : '',
    aobDuration: inputs.benefitDuration ? formatDurationDraft(inputs.benefitDuration.aob[0]) : '',
    cobDuration: inputs.benefitDuration ? formatDurationDraft(inputs.benefitDuration.cob[0]) : '',
    // 0 means unfiltered, so it reads as an empty field rather than a literal "0".
    inflation: inputs.inflationProtection
      ? String(inputs.inflationProtection)
      : inputs.inflationProtectionRange
        ? formatInflationDraft(inputs.inflationProtectionRange.combined)
        : '',
    inflationType: inputs.inflationProtectionRange?.type ?? 'combined',
    aobInflation: inputs.inflationProtectionRange
      ? formatInflationDraft(inputs.inflationProtectionRange.aob)
      : '',
    cobInflation: inputs.inflationProtectionRange
      ? formatInflationDraft(inputs.inflationProtectionRange.cob)
      : '',
    inflationTypeFilter:
      inputs.inflationTypes === null
        ? 'all'
        : inputs.inflationTypes.includes('simple')
          ? 'simple'
          : 'compound',
    draftHiddenCarriers: hiddenCarriers,
    draftBenefitPaymentType: inputs.benefitPaymentType ?? 'all',
  };
}

const EMPTY_DRAFT: DraftState = {
  monthlyBenefit: '',
  monthlyBenefitTiming: 'policy-issue',
  durationType: 'combined',
  duration: '',
  aobDuration: '',
  cobDuration: '',
  inflation: '',
  inflationType: 'combined',
  aobInflation: '',
  cobInflation: '',
  inflationTypeFilter: 'all',
  draftHiddenCarriers: [],
  draftBenefitPaymentType: 'all',
};

function draftReducer(state: DraftState, action: DraftAction): DraftState {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_MONTHLY_BENEFIT_TIMING':
      return { ...state, monthlyBenefitTiming: action.value };
    case 'SET_DURATION_TYPE':
      return { ...state, durationType: action.value };
    case 'SET_INFLATION_TYPE':
      return { ...state, inflationType: action.value };
    case 'SET_INFLATION_TYPE_FILTER':
      return { ...state, inflationTypeFilter: action.value };
    case 'TOGGLE_CARRIER': {
      const isHidden = state.draftHiddenCarriers.includes(action.carrier);
      return {
        ...state,
        draftHiddenCarriers: isHidden
          ? state.draftHiddenCarriers.filter(c => c !== action.carrier)
          : [...state.draftHiddenCarriers, action.carrier],
      };
    }
    case 'SET_BENEFIT_PAYMENT_TYPE':
      return { ...state, draftBenefitPaymentType: action.value };
    case 'HYDRATE':
      return action.value;
    case 'RESET':
      return { ...EMPTY_DRAFT };
    default:
      return state;
  }
}

function removeAppliedFilterFromDraft(
  draft: DraftState,
  removal: BenefitDesignAppliedFilterRemoval,
): DraftState {
  switch (removal.type) {
    case 'benefitPaymentType':
      return { ...draft, draftBenefitPaymentType: 'all' };
    case 'benefitDuration':
      return {
        ...draft,
        durationType: 'combined',
        duration: '',
        aobDuration: '',
        cobDuration: '',
      };
    case 'inflationProtection':
      return {
        ...draft,
        inflationType: 'combined',
        inflation: '',
        aobInflation: '',
        cobInflation: '',
      };
    case 'inflationType':
      return { ...draft, inflationTypeFilter: 'all' };
    case 'carrier':
      return {
        ...draft,
        draftHiddenCarriers: draft.draftHiddenCarriers.filter(
          carrier => carrier !== removal.carrier,
        ),
      };
  }
}

export function useBenefitDesignDraftFilters(availableCarriers: string[] = []) {
  const client = useSelector(selectClient);
  const inputs = useBenefitDesignInputs();
  const hiddenCarriers = useHiddenCarriers();
  const {
    applyMonthlyBenefitSelection,
    setBenefitDuration,
    setInflationProtection,
    setInflationProtectionRange,
    setInflationTypes,
    setBenefitPaymentType,
    reset: resetStore,
  } = useBenefitDesignInputsActions();
  const { setHiddenCarriers, resetCarriers } = useCarrierExclusionsActions();
  const { flags } = useFeatureFlags();
  const useMinimumInflation = flags.inflationProtectionMinimum ?? false;

  const [draft, dispatch] = useReducer(draftReducer, { inputs, hiddenCarriers }, initializeDraft);

  // Saved advisor defaults can arrive after this filter panel has mounted (for example when
  // cached quotes keep the sidebar visible during a reopen). Keep the draft controls aligned
  // with the applied stores instead of leaving them at the values from the first render.
  useEffect(() => {
    dispatch({ type: 'HYDRATE', value: initializeDraft({ inputs, hiddenCarriers }) });
  }, [inputs, hiddenCarriers]);

  const setField = useCallback(
    (field: keyof DraftState, value: string) => dispatch({ type: 'SET_FIELD', field, value }),
    [],
  );

  const setDraftMonthlyBenefitTiming = useCallback(
    (value: MonthlyBenefitTiming) => dispatch({ type: 'SET_MONTHLY_BENEFIT_TIMING', value }),
    [],
  );

  const setDurationType = useCallback(
    (value: SplitMode) => dispatch({ type: 'SET_DURATION_TYPE', value }),
    [],
  );

  const setInflationMode = useCallback(
    (value: SplitMode) => dispatch({ type: 'SET_INFLATION_TYPE', value }),
    [],
  );

  const setInflationTypeFilter = useCallback(
    (value: InflationTypeFilter) => dispatch({ type: 'SET_INFLATION_TYPE_FILTER', value }),
    [],
  );

  const toggleDraftCarrier = useCallback(
    (carrier: string) => dispatch({ type: 'TOGGLE_CARRIER', carrier }),
    [],
  );

  const setDraftBenefitPaymentType = useCallback(
    (value: BenefitPaymentTypeFilter) => dispatch({ type: 'SET_BENEFIT_PAYMENT_TYPE', value }),
    [],
  );

  const applyDraft = useCallback(
    (draftToApply: DraftState) => {
      applyMonthlyBenefitSelection({
        monthlyBenefit: parseCurrency(draftToApply.monthlyBenefit),
        timing: draftToApply.monthlyBenefitTiming,
      });

      if (draftToApply.durationType === 'combined') {
        const dur = parseDuration(draftToApply.duration);
        setBenefitDuration(
          dur !== null
            ? { type: 'combined', combined: [dur, dur], aob: [dur, dur], cob: [dur, dur] }
            : null,
        );
      } else {
        const aob = parseDuration(draftToApply.aobDuration);
        const cob = parseDuration(draftToApply.cobDuration);
        if (aob !== null || cob !== null) {
          const aobRange: [number, number] = aob !== null ? [aob, aob] : [0, Infinity];
          const cobRange: [number, number] = cob !== null ? [cob, cob] : [0, Infinity];
          setBenefitDuration({
            type: 'separate',
            combined: [aobRange[0] + cobRange[0], aobRange[1] + cobRange[1]],
            aob: aobRange,
            cob: cobRange,
          });
        } else {
          setBenefitDuration(null);
        }
      }

      if (useMinimumInflation) {
        setInflationProtection(parsePercent(draftToApply.inflation));
        setInflationProtectionRange(null);
        setInflationTypes(
          draftToApply.inflationTypeFilter === 'all' ? null : [draftToApply.inflationTypeFilter],
        );
      } else if (draftToApply.inflationType === 'combined') {
        const inf = parsePercent(draftToApply.inflation);
        setInflationProtection(null);
        setInflationProtectionRange(
          inf !== null
            ? { type: 'combined', combined: [inf, inf], aob: [inf, inf], cob: [inf, inf] }
            : null,
        );
        setInflationTypes(
          draftToApply.inflationTypeFilter === 'all' ? null : [draftToApply.inflationTypeFilter],
        );
      } else {
        const aob = parsePercent(draftToApply.aobInflation);
        const cob = parsePercent(draftToApply.cobInflation);
        setInflationProtection(null);
        if (aob !== null || cob !== null) {
          const aobRange: [number, number] = aob !== null ? [aob, aob] : [0, MAX_INFLATION_PERCENT];
          const cobRange: [number, number] = cob !== null ? [cob, cob] : [0, MAX_INFLATION_PERCENT];
          setInflationProtectionRange({
            type: 'separate',
            combined: [Math.min(aobRange[0], cobRange[0]), Math.max(aobRange[1], cobRange[1])],
            aob: aobRange,
            cob: cobRange,
          });
        } else {
          setInflationProtectionRange(null);
        }
        setInflationTypes(null);
      }

      setHiddenCarriers(draftToApply.draftHiddenCarriers);
      setBenefitPaymentType(
        draftToApply.draftBenefitPaymentType === 'all'
          ? null
          : draftToApply.draftBenefitPaymentType,
      );
    },
    [
      useMinimumInflation,
      applyMonthlyBenefitSelection,
      setBenefitDuration,
      setInflationProtection,
      setInflationProtectionRange,
      setInflationTypes,
      setHiddenCarriers,
      setBenefitPaymentType,
    ],
  );

  const handleApply = useCallback(() => applyDraft(draft), [applyDraft, draft]);

  const handleRemoveAppliedFilter = useCallback(
    (removal: BenefitDesignAppliedFilterRemoval) => {
      const nextDraft = removeAppliedFilterFromDraft(draft, removal);

      // Removing an active filter refreshes results immediately. Apply the entire current
      // sidebar snapshot so pending edits and the requested removal become active together.
      dispatch({ type: 'HYDRATE', value: nextDraft });
      applyDraft(nextDraft);
    },
    [applyDraft, draft],
  );

  const handleReset = useCallback(() => {
    dispatch({ type: 'RESET' });
    resetStore();
    resetCarriers();
  }, [resetStore, resetCarriers]);

  const { policyIssueAge, careStartAge } = getLtcTiming(
    client?.intakeSurvey?.clientAge,
    client?.appliedInferenceSet?.ltcAtAge,
  );

  return {
    draft,
    useMinimumInflation,
    policyIssueAge,
    careStartAge,
    setField,
    setDraftMonthlyBenefitTiming,
    setDurationType,
    setInflationMode,
    setInflationTypeFilter,
    toggleDraftCarrier,
    setDraftBenefitPaymentType,
    handleApply,
    handleRemoveAppliedFilter,
    handleReset,
  };
}

export type BenefitDesignDraftFiltersController = ReturnType<typeof useBenefitDesignDraftFilters>;
